import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X, Feather } from "lucide-react";
import { Field, Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";

import { TranslatedText } from "@/components/common/text";
import { ClickOutside } from "@/components/common/other/ClickOutside";
import { API } from "@/services/API";
import { cn } from "@/utils/css";

export const Feedback = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState("");
    const [topic, setTopic] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const textAreaRef = useRef(null);
    const feedbackButtonRef = useRef(null);
    const feedbackListRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const topics = ["Functionality", "Design", "Performance issue", "Other"];

    const apiTopic = (topic) => {
        return topic.toLowerCase().replace(" ", "_");
    };

    const handleSubmit = () => {
        try {
            setLoading(true);
            API.post("/feedback", {
                content: `${text} - from path: ${window.location.href}`,
                type: apiTopic(topic || topics[3]),
            })
                .then(() => {
                    setError(null);
                    setSubmitted(true);
                    setText("");
                    setTopic(null);
                    setTimeout(() => {
                        setIsOpen(false);
                    }, 3000);
                })
                .catch((e) => {
                    setSubmitted(true);
                    const errorMessage = e || "An error occurred while submitting feedback.";
                    console.log("Error catch:", errorMessage);
                    setError(errorMessage);
                })
                .finally(() => {
                    setLoading(false);
                    setTimeout(() => {
                        setSubmitted(false);
                    }, 5000);
                });
        } catch (e) {
            console.log("Request failed:", e);
        }
    };
    const handleClose = () => {
        setSubmitted(false);
        if (!isOpen) {
            setIsOpen(true);
            setTimeout(() => {
                textAreaRef.current.focus();
            }, 500);
        } else {
            setIsOpen(false);
        }
    };

    const handleClickAway = (event) => {
        if (
            (!feedbackListRef.current || !feedbackListRef.current.contains(event.target)) &&
            feedbackButtonRef.current &&
            !feedbackButtonRef.current.contains(event.target)
        ) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen && textAreaRef.current) textAreaRef.current.focus();
    }, [isOpen]);

    return (
        <div className="fixed bottom-5 left-5 flex flex-col items-start z-floating">
            <ClickOutside onClick={handleClickAway}>
                <div
                    className={`box-border w-[302px] bg-cm-p-surface rounded-lg overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? "h-[327px] shadow-[0_4px_7px_rgba(51,51,51,0.04)]" : "h-[0px]"}`}
                >
                    <div
                        className={`flex flex-col p-4 w-full h-full justify-end transition-[visibility] duration-500 ease-in-out ${isOpen ? "visible" : "invisible"}`}
                    >
                        {!submitted ? (
                            <>
                                <h4 className="text-cm-p-text-primary text-sm leading-[22px] font-medium px-[2px] mb-4">
                                    <TranslatedText>Share your thoughts with us!</TranslatedText>
                                </h4>
                                <textarea
                                    className="w-full min-h-[152px] h-[152px] px-3 py-2 bg-cm-p-surface-container rounded-lg resize-none text-sm border-none focus:border-none focus:shadow-none focus:ring-0 focus:ring-offset-0 text-cm-p-text-primary placeholder:text-cm-p-text-secondary"
                                    name="feedback"
                                    id="feedback"
                                    placeholder="Enter your suggestion here"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    ref={textAreaRef}
                                />
                                <Field className="flex flex-col bg-cm-p-surface-container h-11 w-full mt-2 rounded-lg">
                                    <Listbox
                                        value={topic}
                                        onChange={(v) => {
                                            setTopic(v);
                                        }}
                                    >
                                        {({ open }) => (
                                            <>
                                                <ListboxButton className="inline-flex flex-row justify-between h-11 px-3 py-2 rounded-lg items-center">
                                                    <span
                                                        className={`text-sm ${topic ? "text-cm-p-text-primary font-medium" : "text-cm-p-text-secondary"}`}
                                                    >
                                                        {topic || <TranslatedText>Select topic</TranslatedText>}
                                                    </span>
                                                    <ChevronDown
                                                        className={`w-4 h-4 text-cm-p-text-secondary transition ease-in ${open ? "rotate-180" : ""}`}
                                                    />
                                                </ListboxButton>
                                                <ListboxOptions
                                                    anchor="top"
                                                    portal={false}
                                                    modal={false}
                                                    className="z-toast-menu w-[270px] [--anchor-gap:4px] py-1"
                                                >
                                                    <div
                                                        ref={feedbackListRef}
                                                        className="flex flex-col rounded-lg bg-cm-p-surface-container border border-cm-p-outline shadow-[0_4px_14px_-3px_rgba(93,93,93,0.08)]"
                                                    >
                                                        {topics.map((item, index) => (
                                                            <ListboxOption
                                                                key={item}
                                                                value={item}
                                                                className="inline-flex justify-center flex-col px-3 h-11 text-cm-p-text-primary text-sm relative cursor-pointer"
                                                            >
                                                                <span>{item}</span>
                                                                {index < topics.length - 1 ? (
                                                                    <span className="absolute bottom-0 left-3 right-3 border-b border-cm-border" />
                                                                ) : null}
                                                            </ListboxOption>
                                                        ))}
                                                    </div>
                                                </ListboxOptions>
                                            </>
                                        )}
                                    </Listbox>
                                </Field>
                                <button
                                    className="w-full min-h-[41px] h-[41px] bg-cm-p-accent-500 rounded mt-3 text-sm text-cm-p-neutral-dark-500 disabled:opacity-40 font-medium"
                                    onClick={handleSubmit}
                                    disabled={!text || loading}
                                >
                                    <TranslatedText>Submit</TranslatedText>
                                </button>
                            </>
                        ) : error ? (
                            <>
                                <div className="text-cm-p-on-container-red mb-[6px] mt-[84px] w-full flex items-center justify-center">
                                    <X size={48} />
                                </div>
                                <div className="text-cm-p-on-container-red text-base text-center flex-1">
                                    <TranslatedText>
                                        Feedback submit error!
                                        <br />
                                        <br />
                                        {error}
                                    </TranslatedText>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="text-cm-p-accent mb-[6px] mt-[84px] w-full flex items-center justify-center">
                                    <Check size={48} />
                                </div>
                                <div className="text-cm-p-text-primary text-base text-center flex-1">
                                    <TranslatedText>
                                        Feedback submitted!
                                        <br />
                                        <br />
                                        Thank you for sharing your opinion and helping us improve.
                                    </TranslatedText>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </ClickOutside>
            <div className="mt-4">
                <button
                    className={cn(
                        "min-w-[52px] h-[52px] bg-cm-p-surface-container rounded-lg inline-flex items-center justify-center text-cm-p-text-secondary shadow-[inset_0_0_0_1px_rgba(0,0,0,0.03)]",
                        {
                            "bg-cm-p-accent-100 pl-5 pr-6": !isOpen,
                        },
                    )}
                    type="button"
                    onClick={handleClose}
                    ref={feedbackButtonRef}
                >
                    {isOpen ? (
                        <X size={24} />
                    ) : (
                        <span className="text-cm-p-text-primary font-medium inline-flex gap-2 items-center">
                            <Feather className="w-4 h-4" />
                            <TranslatedText className="text-sm">Leave feedback</TranslatedText>
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
};
